import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScoreIcon from '@mui/icons-material/Score';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ConfirmDialog from "../../../components/ConfirmDialog";
import CustomSnackbar from "../../../components/CustomSnackbar";
import MiniPuzRow from "../../../pzl_common/MiniPuzRow";
import VerifyDialog from "../../../pzl_common/VerifyDialog";
import ResolvePuzzleDialog from "../../../pzl_common/ResolvePuzzleDialog";
import { DIRECTION_DIAGONAL_LEFT_UP, DIRECTION_DIAGONAL_RIGHT_UP, DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../../pzl_common/PuzzleUtil";
import WordMeaning from "../../../pzl_common/WordMeaning";
import WordRedefine from "../../../pzl_common/WordRedefine";
import BorderHeader from "../../../pzl_common/BorderHeader";
import GridFillDialog from "./GridFillDialog";
import WordNetworkDialog from "../../../pzl_common/WordNetworkDialog";
import WordScoreDialog from "../../../pzl_common/WordScoreDialog";
import AdminGridBoardService, { DRAFT, DISABLED, PUBLISHED, READY_TO_REVIEW, REVIEWED } from "../AdminGridBoardService";
import { usePuzzleData } from "../usePuzzleData";
import GridCanAssoc from "./GridCanAssoc";
import GridCandManual from "./GridCandManual";
import GridCandAI from "./GridCandAI";
import { generatePositions, getGridWord } from "./GridCandUtils";
import AddBannedWordDialog from "../../../setting/banned_words/AddBannedWordDialog";
import BannedService from "../../../setting/banned_words/BannedService";
import BannedWordsPopup from "../../../setting/banned_words/BannedWordsPopup";
import ApiService from "../../../../services/ApiService";
import Logger from "../../../../services/Logger";
import UserService from "../../../../services/UserService";
import ValidationDialog from "../../../../creator/common/ValidationDialog";
import { resolveTplGridDims, resolveTplGridSize } from "../gridTplSize";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

export default function GridCandEdit() {
    const { gridId } = useParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [refresh, setRefresh] = useState(0);
    const { hwTpl: gridTpl, loading, error: fetchError } = usePuzzleData(gridId, refresh);

    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedAssociatedWord, setSelectedAssociatedWord] = useState(null);
    const [selectedNetworkWord, setSelectedNetworkWord] = useState(null);
    const abortControllerRef = useRef(null);
    const gridContainerRef = useRef(null);
    const gridContentRef = useRef(null);
    const [gridScale] = useState(1);

    // Mode state - use ref to preserve across word additions
    const modeRef = useRef('assoc');
    const [, setMode] = useState(() => modeRef.current);

    const [selectedManualWord, setSelectedManualWord] = useState(null);

    // Snackbar state
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Discard confirmation dialog state
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

    // Disable confirmation dialog state
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);

    // Word to delete state
    const [wordToDelete, setWordToDelete] = useState(null);

    // Puzzle type selection dialog state
    const [puzzleTypeDialogOpen, setPuzzleTypeDialogOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [resolveDialogType, setResolveDialogType] = useState("duplicates");

    // Title hook loading state
    const [loadingApplyTitleHook, setLoadingApplyTitleHook] = useState(false);
    // Title hook confirmation dialog state
    const [showTitleHookDialog, setShowTitleHookDialog] = useState(false);

    // WordRedefine dialog state
    const [openWordRedefineDialog, setOpenWordRedefineDialog] = useState(false);
    const [editingDefinition, setEditingDefinition] = useState("");
    const [editingWord, setEditingWord] = useState("");

    // Banned words popup state
    const [openBannedWordsPopup, setOpenBannedWordsPopup] = useState(false);
    const [openAddBannedWordDialog, setOpenAddBannedWordDialog] = useState(false);
    const [selectedWordForBanned, setSelectedWordForBanned] = useState("");
    const [loadingAddBanned, setLoadingAddBanned] = useState(false);

    // Word Network dialog state
    const [networkDialogOpen, setNetworkDialogOpen] = useState(false);

    // Validation dialog state
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);

    // Fill common dialog state (Fill button)
    const [fillCommonDialogOpen, setFillCommonDialogOpen] = useState(false);
    const [loadingMatchKeywordAdd, setLoadingMatchKeywordAdd] = useState(false);
    const [loadingFillPatternAdd, setLoadingFillPatternAdd] = useState(false);

    // Word Score dialog state
    const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
    const [wordScores, setWordScores] = useState([]);
    const [loadingScores, setLoadingScores] = useState(false);
    const [loadingRearrange, setLoadingRearrange] = useState(false);
    const [loadingFillRandom, setLoadingFillRandom] = useState(false);

    const [placementWordFromFill, setPlacementWordFromFill] = useState(null);
    const [isManager, setIsManager] = useState(false);
    const [draftDialogOpen, setDraftDialogOpen] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(false);
    const [readyToReviewDialogOpen, setReadyToReviewDialogOpen] = useState(false);
    const [loadingReadyToReview, setLoadingReadyToReview] = useState(false);
    const [reviewedDialogOpen, setReviewedDialogOpen] = useState(false);
    const [loadingReviewed, setLoadingReviewed] = useState(false);
    const [backToReviewedDialogOpen, setBackToReviewedDialogOpen] = useState(false);
    const [loadingBackToReviewed, setLoadingBackToReviewed] = useState(false);

    // Square state - initialize from filledCoordinates
    const [square, setSquare] = useState([]);

    // Memoized parsed filledCoordinates
    const filledCoordinates = useMemo(() => {
        return gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
    }, [gridTpl?.filledCoordinates]);

    // Find the scattered word (hidden message) from filledCoordinates
    const scatteredWord = useMemo(() => {
        return filledCoordinates.find(coord => coord.direction === "scattered");
    }, [filledCoordinates]);

    // Board size: 9 or 12 (HIDDEN_MESSAGE is always 12)
    const { rows: gridRowsCount, cols: gridColsCount } = resolveTplGridDims(gridTpl);
    const gridSize = resolveTplGridSize(gridTpl);

    // Initialize square from filledCoordinates
    useEffect(() => {
        if (filledCoordinates.length > 0) {
            const newSquare = initializeSquare(filledCoordinates);
            setSquare(newSquare);
        } else {
            // Even if filledCoordinates is empty, initialize an empty NxN grid
            const newSquare = initializeSquare([]);
            setSquare(newSquare);
        }
    }, [filledCoordinates, gridSize]);

    // Function to initialize the square array
    const initializeSquare = (filledCoordinates) => {
        const size = gridSize;
        let newSquare = Array.from({ length: size }, () => Array(size).fill(0));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        let found = "";

        if (filledCoordinates) {
            found = filledCoordinates
                .map((c) => c.positions)
                .map((positions) => {
                    const matchingPosition = positions.find((p) => p.row === row && p.col === col);

                    if (matchingPosition && matchingPosition.value) {
                        // Normalize French accents (remove diacritical marks) before converting to uppercase
                        const char = matchingPosition.value;
                        const normalized = char
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, ''); // Remove diacritical marks
                        return normalized.toUpperCase();
                    }
                    return "";
                })
                .find((value) => value !== "");
        }

        return found || "";
    };

    // Check if puzzle has word network data
    const hasWordNetwork = useMemo(() => {
        const wordNetwork = gridTpl?.template?.wordNetwork || gridTpl?.wordNetwork;
        return wordNetwork?.nodes && wordNetwork?.links;
    }, [gridTpl]);

    // Get word network data
    const wordNetworkData = useMemo(() => {
        return gridTpl?.template?.wordNetwork || gridTpl?.wordNetwork;
    }, [gridTpl]);

    const GRID_TOTAL_CELLS = gridRowsCount * gridColsCount; // Mini rows×cols or Classic 12×12
    const { cellsFullyFilled, emptyCellCount } = useMemo(() => {
        const uniqueCells = new Set();
        filledCoordinates.forEach(word => {
            if (word.text && word.positions) {
                word.positions.forEach(pos => { uniqueCells.add(`${pos.row}-${pos.col}`); });
            }
        });
        return {
            cellsFullyFilled: uniqueCells.size >= GRID_TOTAL_CELLS,
            emptyCellCount: Math.max(0, GRID_TOTAL_CELLS - uniqueCells.size)
        };
    }, [filledCoordinates, GRID_TOTAL_CELLS]);

    const wordsNotInUse = useMemo(() => {
        const raw = gridTpl?.wordsNotInUse;
        if (!raw || typeof raw !== "string") return [];
        return raw
            .split("\n")
            .map((w) => w.trim())
            .filter((w) => w.length > 0);
    }, [gridTpl?.wordsNotInUse]);

    useEffect(() => {
        let cancelled = false;
        UserService.getUserInfo().then((u) => {
            const role = u?.cloudUser?.userRole;
            if (!cancelled && role && String(role).toUpperCase() === "MANAGER") {
                setIsManager(true);
            }
        }).catch(() => { });
        return () => { cancelled = true; };
    }, []);

    // Sync mode state with ref when gridTpl updates
    useEffect(() => {
        setMode(modeRef.current);
    }, [gridTpl?.id]);

    // Check if we're in creator context
    const isCreatorContext = pathname.startsWith('/creator');

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);


    const handleSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleWordSelect = async (candidate) => {
        // Validate candidate object
        if (!candidate) {
            Logger.error("handleWordSelect called with null/undefined candidate");
            return;
        }

        // Clear network word selection when selecting from other sources
        setSelectedNetworkWord(null);

        // Normalize candidate to ensure it has word property and positions
        const normalizedCandidate = {
            ...candidate,
            word: candidate.word || candidate.text || "",
            // Ensure positions array exists
            positions: candidate.positions && Array.isArray(candidate.positions) ? candidate.positions : []
        };

        // Check if this is an associated word (has specific properties that distinguish it from manual candidates)
        // Associated words from GridCanAssoc have positions array and word property
        // They also typically have additional properties like startRow, startCol, direction, pattern, etc.
        if (normalizedCandidate.positions && normalizedCandidate.positions.length > 0 && normalizedCandidate.word &&
            (normalizedCandidate.startRow !== undefined || normalizedCandidate.startCol !== undefined || normalizedCandidate.direction || normalizedCandidate.pattern)) {
            // This is an associated word from the association mode
            setSelectedAssociatedWord(normalizedCandidate);
            setSelectedWord(null);
        } else {
            // This is a regular word from manual mode
            setSelectedWord(normalizedCandidate);
            setSelectedAssociatedWord(null);
        }
    };

    const handleWordAdd = async (candidate) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!gridTpl?.filledCoordinates) {
                handleSnackbar("No puzzle data available", "error");
                return;
            }

            // Validate candidate object
            if (!candidate) {
                handleSnackbar("Invalid word candidate", "error");
                return;
            }

            // Use candidate.word if available, otherwise use candidate.text
            let wordText = candidate.word || candidate.text;
            if (!wordText) {
                handleSnackbar("Word candidate has no text content", "error");
                return;
            }

            // Normalize French words by removing accents
            const language = gridTpl?.language || 'english';
            if (language === 'french') {
                wordText = normalizeFrenchWord(wordText);
            } else {
                wordText = wordText.toLowerCase();
            }

            const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);

            // Create new word entry
            // When isReversed is true, the positions array already has the correct values
            // in pos.value from generatePositions and mapWordToPositions, so we should use those
            const isReversed = candidate.isReversed || false;
            const newWordEntry = {
                id: uuidv4(),
                text: wordText,
                direction: candidate.direction,
                length: wordText.length,
                reversed: isReversed,
                positions: candidate.positions.map((pos, index) => {
                    // Always prefer pos.value if it's set (it's correct from generatePositions/mapWordToPositions)
                    // This is especially important for reversed words where the character mapping is complex
                    if (pos.value !== undefined && pos.value !== "" && pos.value !== null) {
                        return {
                            ...pos,
                            value: pos.value.toLowerCase() // Ensure lowercase for consistency
                        };
                    }
                    // Fallback: map the word characters correctly
                    // For diagonal directions with isReversed, position 0 should have word[0]
                    // For horizontal/vertical with isReversed, position 0 should have word[length-1]
                    const isDiagonal = candidate.direction === DIRECTION_DIAGONAL_RIGHT_UP || candidate.direction === DIRECTION_DIAGONAL_LEFT_UP;
                    const charIndex = isReversed
                        ? (isDiagonal ? index : (wordText.length - 1 - index))
                        : index;
                    const charValue = (charIndex >= 0 && charIndex < wordText.length)
                        ? wordText[charIndex]
                        : (pos.value || "");
                    return {
                        ...pos,
                        value: charValue.toLowerCase()
                    };
                })
            };

            // Add to filledCoordinates
            const updatedCoordinates = [...filledCoordinates, newWordEntry];

            // Update puzzle via API
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedCoordinates, wordText);

            handleSnackbar(`Word "${wordText}" added successfully!`, "success");

            // Refresh puzzle data
            setRefresh(prev => prev + 1);

        } catch (error) {
            Logger.error("Error adding new word:", error);
            handleSnackbar("Error adding new word", "error");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleBack = () => {
        navigate(`/admin/puzzle/grid_board/draft/${gridId}`);
    };

    const handleModeChange = (_event, newMode) => {
        if (newMode !== null) {
            // Update ref immediately to persist across re-renders
            modeRef.current = newMode;
            setMode(newMode);
            // Clear selections when switching modes
            setSelectedWord(null);
            setSelectedAssociatedWord(null);
            setSelectedManualWord(null);
            setSelectedNetworkWord(null);
        }
    };

    const handleManualWordSelect = async (candidate) => {
        // Clear network word selection when selecting manual word
        setSelectedNetworkWord(null);
        setSelectedManualWord(candidate);
    };

    const handleManualWordAdd = async (candidate) => {
        try {
            // Validate candidate object
            if (!candidate) {
                handleSnackbar("Invalid manual word candidate", "error");
                return;
            }

            // gridWord: normalized for placement (no hyphens/spaces) - used for positions
            // displayText: original form for storage/display (e.g., "run on", "ex-wife")
            const gridWord = candidate.gridWord || getGridWord(candidate.word || candidate.text || "");
            const displayText = candidate.word || candidate.text || gridWord;
            if (!gridWord) {
                handleSnackbar("Manual word candidate has no text content", "error");
                return;
            }

            // Normalize grid word for French (accents) - used only for position mapping
            const language = gridTpl?.language || 'english';
            let normalizedGridWord = gridWord;
            if (language === 'french') {
                normalizedGridWord = normalizeFrenchWord(normalizedGridWord);
            } else {
                normalizedGridWord = normalizedGridWord.toLowerCase();
            }

            // Create the new word entry
            // text: display form (e.g., "run on"); length/positions: based on gridWord (e.g., "runon" = 5 cells)
            const isReversed = candidate.isReversed || false;
            const newWordEntry = {
                id: uuidv4(),
                text: displayText,
                direction: candidate.direction,
                length: normalizedGridWord.length,
                positions: candidate.positions.map((pos, index) => {
                    // Always prefer pos.value if it's set (it's correct from generatePositions)
                    if (pos.value !== undefined && pos.value !== "" && pos.value !== null) {
                        return {
                            ...pos,
                            value: pos.value.toLowerCase()
                        };
                    }
                    // Fallback: map from normalizedGridWord
                    const isDiagonal = candidate.direction === DIRECTION_DIAGONAL_RIGHT_UP || candidate.direction === DIRECTION_DIAGONAL_LEFT_UP;
                    const charIndex = isReversed
                        ? (isDiagonal ? index : (normalizedGridWord.length - 1 - index))
                        : index;
                    const charValue = (charIndex >= 0 && charIndex < normalizedGridWord.length)
                        ? normalizedGridWord[charIndex]
                        : (pos.value || "");
                    return {
                        ...pos,
                        value: charValue.toLowerCase()
                    };
                })
            };

            // Add to filled coordinates
            const updatedFilledCoordinates = [...(gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : []), newWordEntry];

            // Update via API
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, newWordEntry.text);

            handleSnackbar(`Word "${displayText}" added successfully!`, "success");

            // Refresh puzzle data
            setRefresh(prev => prev + 1);

            // Reset manual selection
            setSelectedManualWord(null);

        } catch (error) {
            Logger.error("Error adding manual word:", error);
            handleSnackbar("Error adding word. Please try again.", "error");
        }
    };

    // Cell click handler: only used to select position for placing a word (manual/AI mode). No word highlighting.
    const handleCellClick = (row, col) => {
        // Manual and AI mode: update selected word position for place-a-word
        if (modeRef.current === 'manual' || modeRef.current === 'ai') {
            if (selectedManualWord) {
                // Update the selected manual word's position
                const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word || '') || '';
                const updatedWord = {
                    ...selectedManualWord,
                    startRow: row,
                    startCol: col,
                    positions: generatePositions(row, col, selectedManualWord.direction, gridWord.length, gridWord, selectedManualWord.isReversed)
                };
                setSelectedManualWord(updatedWord);
            } else {
                // Even if no word is selected, update the position by creating a temporary word object
                // This will trigger the useEffect in GridCandManual/GridCandAI to update row/col and refresh candidates
                const tempWord = {
                    startRow: row,
                    startCol: col,
                    direction: DIRECTION_HORIZONTAL,
                    isReversed: false
                };
                setSelectedManualWord(tempWord);
                // Clear it immediately after the useEffect processes it
                setTimeout(() => {
                    setSelectedManualWord(prev => {
                        // Only clear if it's still the temp word (has no word property)
                        if (prev && !prev.word) {
                            return null;
                        }
                        return prev;
                    });
                }, 100);
            }
        }
    };

    const handleDiscardPuzzle = async () => {
        try {
            await AdminGridBoardService.markAsDiscarded(gridTpl.id);
            handleSnackbar("Puzzle discarded successfully", "success");
            navigate(`/admin/puzzle/grid_board/draft/${gridId}`);
        } catch (error) {
            Logger.error("Error discarding puzzle:", error);
            handleSnackbar("Error discarding puzzle", "error");
        }
    };

    const handleDisablePuzzle = async () => {
        try {
            await AdminGridBoardService.updateHwTplState(gridTpl.id, DISABLED);
            handleSnackbar("Puzzle disabled successfully", "success");
            navigate(`/admin/puzzle/grid_board/draft/${gridId}`);
        } catch (error) {
            Logger.error("Error disabling puzzle:", error);
            handleSnackbar("Error disabling puzzle", "error");
        }
    };

    const handleDeleteWordFromNetwork = async (wordText) => {
        if (!wordText) return;

        // Set the word to delete and show confirmation dialog
        setWordToDelete(wordText);
        setDiscardDialogOpen(true);
    };

    const handleConfirmDeleteWord = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!wordToDelete) return;

            // Get current filled coordinates and remove the word's positions
            const filledCoords = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            const updatedCoords = filledCoords.filter(coord =>
                coord.text && coord.text.toUpperCase() !== wordToDelete?.toUpperCase()
            );

            // Call API to update the puzzle
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedCoords, null);

            handleSnackbar(`Word "${wordToDelete}" removed successfully`, "success");

            // Refresh puzzle data
            setRefresh(prev => prev + 1);
            setWordToDelete(null);
            setDiscardDialogOpen(false);

        } catch (error) {
            Logger.error("Error deleting word:", error);
            handleSnackbar("Error deleting word", "error");
            setDiscardDialogOpen(false);
            setWordToDelete(null);
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleApplyTitleHook = () => {
        // Show confirmation dialog first
        setShowTitleHookDialog(true);
    };

    const confirmApplyTitleHook = async (targetId) => {
        if (!gridTpl?.id) return;

        try {
            setLoadingApplyTitleHook(true);
            const result = await AdminGridBoardService.applyTitleHook(gridTpl.id);

            if (result.code && result.code === "FAILURE") {
                handleSnackbar(result.message || "Failed to apply title hook", "error");
            } else {
                handleSnackbar("Title hook applied successfully", "success");
                setRefresh(prev => prev + 1);
            }
        } catch (err) {
            Logger.error("Error applying title hook:", err);
            handleSnackbar("Failed to apply title hook. Please try again.", "error");
        } finally {
            setLoadingApplyTitleHook(false);
        }
    };

    const handleRearrangeWords = async () => {
        const tooLong = [gridTpl?.wordsInUse, gridTpl?.wordsNotInUse]
            .filter(Boolean)
            .flatMap((block) => String(block).split("\n"))
            .map((w) => w.trim())
            .filter((w) => w.length > gridSize);
        if (tooLong.length > 0) {
            const sample = [...new Set(tooLong)].slice(0, 5).join(", ");
            handleSnackbar(
                `Words longer than ${gridSize} letters cannot be placed on this board: ${sample}`,
                "error"
            );
            return;
        }
        setLoadingRearrange(true);
        try {
            await AdminGridBoardService.rearrangeWords(gridTpl.id);
            handleSnackbar("Words rearranged successfully", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error rearranging words:", err);
            handleSnackbar(err.message || "Failed to rearrange words. Please try again.", "error");
        } finally {
            setLoadingRearrange(false);
        }
    };

    const handleFillRandomLetters = async () => {
        if (!gridTpl?.id || cellsFullyFilled) {
            return;
        }
        setLoadingFillRandom(true);
        try {
            const result = await AdminGridBoardService.fillRandomLetters(gridTpl.id);
            if (result?.code === "FAILURE") {
                handleSnackbar(result.message || "Failed to fill random letters", "error");
            } else {
                handleSnackbar(result?.message || "Empty cells filled with random letters", "success");
                setRefresh((prev) => prev + 1);
            }
        } catch (err) {
            Logger.error("Error filling random letters:", err);
            handleSnackbar(err.message || "Failed to fill random letters. Please try again.", "error");
        } finally {
            setLoadingFillRandom(false);
        }
    };

    const mapApiDirectionToGrid = (dir) => {
        if (!dir) return DIRECTION_HORIZONTAL;
        const d = String(dir).toLowerCase();
        if (d === "vertical" || d === "down") return DIRECTION_VERTICAL;
        if (d === "horizontal" || d === "right" || d === "left") return DIRECTION_HORIZONTAL;
        if (d === "diagonal-right-up" || d === "diagonal-left-down") return DIRECTION_DIAGONAL_RIGHT_UP;
        if (d === "diagonal-left-up" || d === "diagonal-right-down") return DIRECTION_DIAGONAL_LEFT_UP;
        return DIRECTION_HORIZONTAL;
    };

    const handleFillPatternAdd = async (item) => {
        if (!gridTpl?.id || !item?.word) return;
        setLoadingFillPatternAdd(true);
        try {
            const displayWord = (item.word || "").trim();
            const gridWord = getGridWord(displayWord.toLowerCase());
            const direction = mapApiDirectionToGrid(item.direction);

            const rawPositions = item.positions || [];
            let startPos = null;
            if (rawPositions.length > 0) {
                const sorted = [...rawPositions].filter((p) => (p.row != null && p.col != null));
                if (direction === DIRECTION_HORIZONTAL) {
                    startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                } else if (direction === DIRECTION_VERTICAL) {
                    startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                } else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) {
                    startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                } else if (direction === DIRECTION_DIAGONAL_LEFT_UP) {
                    startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                } else {
                    startPos = sorted[0];
                }
            }
            const positions = startPos
                ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false)
                : rawPositions
                    .filter((p) => (p.letter || p.value || "").trim() !== "")
                    .sort((a, b) =>
                        direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP
                            ? a.col - b.col
                            : a.row - b.row
                    )
                    .slice(0, gridWord.length)
                    .map((p, i) => ({
                        row: p.row,
                        col: p.col,
                        value: gridWord[i] || ""
                    }));

            const existingFilled = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            const existingCellMap = getExistingCellMap(existingFilled);
            const conflicts = [];
            (positions || []).forEach((p) => {
                const key = `${p.row}-${p.col}`;
                const newVal = (p.value || "").trim();
                const newNormalized = normalizeFrenchWord(newVal);
                if (!newNormalized) return;
                const existing = existingCellMap.get(key);
                if (existing && existing.normalized !== newNormalized) {
                    conflicts.push({ row: p.row, col: p.col, existing: existing.raw, new: newVal });
                }
            });
            if (conflicts.length > 0) {
                const msg = conflicts
                    .map((c) => `(${c.row + 1},${c.col + 1}) has '${c.existing}' but new word has '${c.new}'`)
                    .join("; ");
                handleSnackbar(msg, "error");
                setLoadingFillPatternAdd(false);
                return;
            }

            const newWordEntry = {
                id: uuidv4(),
                text: displayWord,
                direction,
                length: gridWord.length,
                reversed: false,
                positions
            };

            const updatedFilledCoordinates = [...existingFilled, newWordEntry];

            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, gridWord);

            handleSnackbar(`Word "${displayWord}" added successfully!`, "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding candidate word:", err);
            handleSnackbar(err?.message || "Failed to add word. Please try again.", "error");
        } finally {
            setLoadingFillPatternAdd(false);
        }
    };

    const inferMatchKeywordDirection = (positions) => {
        if (!Array.isArray(positions) || positions.length <= 1) return DIRECTION_HORIZONTAL;
        const sorted = [...positions].sort((a, b) => (a.row !== b.row ? a.row - b.row : a.col - b.col));
        const sameRow = sorted.every((p) => p.row === sorted[0].row);
        const sameCol = sorted.every((p) => p.col === sorted[0].col);
        if (sameRow) return DIRECTION_HORIZONTAL;
        if (sameCol) return DIRECTION_VERTICAL;
        const dRow = sorted[1].row - sorted[0].row;
        const dCol = sorted[1].col - sorted[0].col;
        if (dRow === dCol) return DIRECTION_DIAGONAL_LEFT_UP;
        if (dRow === -dCol) return DIRECTION_DIAGONAL_RIGHT_UP;
        return DIRECTION_HORIZONTAL;
    };

    const getExistingCellMap = (filledCoords) => {
        const map = new Map();
        if (!filledCoords || !Array.isArray(filledCoords)) return map;
        filledCoords.forEach((entry) => {
            (entry.positions || []).forEach((p) => {
                if (p.row != null && p.col != null && (p.value || "").trim() !== "") {
                    const key = `${p.row}-${p.col}`;
                    const normalized = normalizeFrenchWord((p.value || "").trim());
                    if (normalized) map.set(key, { raw: (p.value || "").trim(), normalized });
                }
            });
        });
        return map;
    };

    const addWordFromResult = async (item, setResult) => {
        if (!gridTpl?.id || !item?.word) return;
        setLoadingMatchKeywordAdd(true);
        try {
            const coords = item.coordinates;
            const displayWord = (item.word || coords?.text || coords?.word || "").trim();
            const gridWord = getGridWord(displayWord.toLowerCase());

            let direction;
            let positions;
            let reversed = false;

            if (coords && Array.isArray(coords.positions) && coords.positions.length > 0) {
                direction = mapApiDirectionToGrid(coords.direction);
                reversed = coords.reversed ?? false;
                const raw = coords.positions.slice(0, gridWord.length);
                positions = raw.map((p, i) => {
                    const charIndex = reversed ? gridWord.length - 1 - i : i;
                    return {
                        row: p.row,
                        col: p.col,
                        value: (gridWord[charIndex] ?? "").toLowerCase()
                    };
                });
            } else {
                const rawPositions = item.positions || [];
                direction = inferMatchKeywordDirection(rawPositions);
                let startPos = null;
                if (rawPositions.length > 0) {
                    const sorted = [...rawPositions].filter((p) => p.row != null && p.col != null);
                    if (direction === DIRECTION_HORIZONTAL) {
                        startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                    } else if (direction === DIRECTION_VERTICAL) {
                        startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                    } else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) {
                        startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                    } else if (direction === DIRECTION_DIAGONAL_LEFT_UP) {
                        startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                    } else {
                        startPos = sorted[0];
                    }
                }
                positions = startPos
                    ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false)
                    : rawPositions
                        .filter((p) => (p.letter || p.value || "").trim() !== "")
                        .sort((a, b) =>
                            direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP
                                ? a.col - b.col
                                : a.row - b.row
                        )
                        .slice(0, gridWord.length)
                        .map((p, i) => ({ row: p.row, col: p.col, value: gridWord[i] || "" }));
            }

            const existingFilled = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            const existingCellMap = getExistingCellMap(existingFilled);
            const conflicts = [];
            (positions || []).forEach((p) => {
                const key = `${p.row}-${p.col}`;
                const newVal = (p.value || "").trim();
                const newNormalized = normalizeFrenchWord(newVal);
                if (!newNormalized) return;
                const existing = existingCellMap.get(key);
                if (existing && existing.normalized !== newNormalized) {
                    conflicts.push({ row: p.row, col: p.col, existing: existing.raw, new: newVal });
                }
            });
            if (conflicts.length > 0) {
                const msg = conflicts
                    .map((c) => `(${c.row + 1},${c.col + 1}) has '${c.existing}' but new word has '${c.new}'`)
                    .join("; ");
                handleSnackbar(msg, "error");
                setLoadingMatchKeywordAdd(false);
                return;
            }

            const newWordEntry = {
                id: uuidv4(),
                text: displayWord,
                direction,
                length: gridWord.length,
                reversed,
                positions
            };

            const updatedFilledCoordinates = [...existingFilled, newWordEntry];

            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, gridWord);

            handleSnackbar(`Word "${displayWord}" added successfully!`, "success");
            setRefresh((prev) => prev + 1);
            if (setResult) {
                setResult((prev) => {
                    if (!prev?.wordsWithPositions) return prev;
                    const next = prev.wordsWithPositions.filter((w) => w !== item);
                    return { wordsFound: next.length, wordsWithPositions: next };
                });
            }
        } catch (err) {
            Logger.error("Error adding word:", err);
            handleSnackbar(err?.message || "Failed to add word. Please try again.", "error");
        } finally {
            setLoadingMatchKeywordAdd(false);
        }
    };

    const addWordsFromResult = async (items) => {
        if (!gridTpl?.id || !Array.isArray(items) || items.length === 0) return;
        setLoadingMatchKeywordAdd(true);
        try {
            const existingFilled = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            let currentFilled = [...existingFilled];
            const newEntries = [];
            let skippedNoPositions = 0;

            for (const item of items) {
                const coords = item.coordinates;
                const displayWord = (item.word ?? item.text ?? coords?.text ?? coords?.word ?? item.info?.word ?? "").toString().trim();
                if (!displayWord) continue;
                const gridWord = getGridWord(displayWord.toLowerCase());

                // Support both API shapes: coordinates.positions (Bedrock fill APIs) and top-level positions (match-keyword).
                // Use whichever has positions (API sometimes fills only one of the two per item).
                const fromCoords = coords && Array.isArray(coords.positions) && coords.positions.length > 0 ? coords.positions : [];
                const fromTop = Array.isArray(item.positions) && item.positions.length > 0 ? item.positions : [];
                const rawPositionsFromApi = fromCoords.length > 0 ? fromCoords : fromTop;

                let direction;
                let positions;
                let reversed = false;

                if (rawPositionsFromApi.length > 0 && coords && Array.isArray(coords.positions) && coords.positions.length > 0) {
                    direction = mapApiDirectionToGrid(coords.direction);
                    reversed = coords.reversed ?? false;
                    const raw = rawPositionsFromApi.slice(0, gridWord.length);
                    positions = raw.map((p, i) => {
                        const charIndex = reversed ? gridWord.length - 1 - i : i;
                        return { row: p.row, col: p.col, value: (gridWord[charIndex] ?? "").toLowerCase() };
                    });
                } else if (rawPositionsFromApi.length > 0) {
                    const rawPositions = rawPositionsFromApi;
                    direction = inferMatchKeywordDirection(rawPositions);
                    let startPos = null;
                    const sorted = [...rawPositions].filter((p) => p.row != null && p.col != null);
                    if (sorted.length > 0) {
                        if (direction === DIRECTION_HORIZONTAL) startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                        else if (direction === DIRECTION_VERTICAL) startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                        else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                        else if (direction === DIRECTION_DIAGONAL_LEFT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                        else startPos = sorted[0];
                    }
                    positions = startPos
                        ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false)
                        : rawPositions
                            .filter((p) => (p.letter || p.value || "").trim() !== "")
                            .sort((a, b) => (direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP ? a.col - b.col : a.row - b.row))
                            .slice(0, gridWord.length)
                            .map((p, i) => ({ row: p.row, col: p.col, value: gridWord[i] || "" }));
                } else {
                    skippedNoPositions += 1;
                    continue;
                }

                if (!positions || positions.length === 0) {
                    skippedNoPositions += 1;
                    continue;
                }

                const cellMap = getExistingCellMap(currentFilled);
                const conflicts = [];
                (positions || []).forEach((p) => {
                    const key = `${p.row}-${p.col}`;
                    const newVal = (p.value || "").trim();
                    const newNormalized = normalizeFrenchWord(newVal);
                    if (!newNormalized) return;
                    const existing = cellMap.get(key);
                    if (existing && existing.normalized !== newNormalized) {
                        conflicts.push({ row: p.row, col: p.col, existing: existing.raw, new: newVal });
                    }
                });
                if (conflicts.length > 0) {
                    const msg = `"${displayWord}": ${conflicts.map((c) => `(${c.row + 1},${c.col + 1}) has '${c.existing}' but new word has '${c.new}'`).join("; ")}`;
                    handleSnackbar(msg, "error");
                    continue;
                }

                const newWordEntry = {
                    id: uuidv4(),
                    text: displayWord,
                    direction,
                    length: gridWord.length,
                    reversed,
                    positions
                };
                newEntries.push(newWordEntry);
                currentFilled = [...currentFilled, newWordEntry];
            }

            if (newEntries.length === 0) {
                if (skippedNoPositions > 0) {
                    handleSnackbar(`${skippedNoPositions} word(s) skipped (no position data). Try placing one at a time.`, "warning");
                }
                setLoadingMatchKeywordAdd(false);
                return;
            }

            // Persist each word with cumulative state so the backend applies all (workaround when backend only applies one word per request)
            let filledSoFar = [...existingFilled];
            for (const entry of newEntries) {
                filledSoFar = [...filledSoFar, entry];
                const wordForApi = getGridWord((entry.text || "").toLowerCase());
                await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, filledSoFar, wordForApi);
            }

            const wordList = newEntries.map((e) => e.text).filter(Boolean).join(", ");
            const count = newEntries.length;
            const successMsg = count === 1 ? `"${wordList}" added successfully!` : `${count} words added: ${wordList}`;
            const skipMsg = skippedNoPositions > 0 ? ` ${skippedNoPositions} word(s) skipped (no position data).` : "";
            handleSnackbar(successMsg + skipMsg, skippedNoPositions > 0 ? "warning" : "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding words:", err);
            handleSnackbar(err?.message || "Failed to add words. Please try again.", "error");
        } finally {
            setLoadingMatchKeywordAdd(false);
        }
    };

    const handleReadyForReview = () => {
        setPuzzleTypeDialogOpen(true);
    };

    const handleDraftClick = () => setDraftDialogOpen(true);

    const handleReviewedClick = () => {
        if (gridTpl?.state === PUBLISHED) setBackToReviewedDialogOpen(true);
        else setReviewedDialogOpen(true);
    };

    const handleStateBack = () => {
        const s = gridTpl?.state;
        if (s === READY_TO_REVIEW) handleDraftClick();
        else if (s === REVIEWED) setReadyToReviewDialogOpen(true);
        else if (s === PUBLISHED) setBackToReviewedDialogOpen(true);
    };

    const handleStateForward = () => {
        const s = gridTpl?.state;
        if (s === DRAFT) handleReadyForReview();
        else if (s === READY_TO_REVIEW && isManager) handleReviewedClick();
    };

    const confirmDraft = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingDraft(true);
            await AdminGridBoardService.updateHwTplState(gridTpl.id, DRAFT);
            handleSnackbar("Puzzle moved back to Draft", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving puzzle to Draft:", err);
            handleSnackbar(err?.message || "Failed to update state to Draft", "error");
        } finally {
            setLoadingDraft(false);
            setDraftDialogOpen(false);
        }
    };

    const confirmReadyToReview = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingReadyToReview(true);
            await AdminGridBoardService.updateHwTplState(gridTpl.id, READY_TO_REVIEW);
            handleSnackbar("Puzzle moved to Ready to Review", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving to Ready to Review:", err);
            handleSnackbar(err?.message || "Failed to update state", "error");
        } finally {
            setLoadingReadyToReview(false);
            setReadyToReviewDialogOpen(false);
        }
    };

    const confirmReviewed = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingReviewed(true);
            await AdminGridBoardService.updateHwTplState(gridTpl.id, REVIEWED);
            handleSnackbar("Puzzle marked as Reviewed", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error marking puzzle as Reviewed:", err);
            handleSnackbar(err?.message || "Failed to update state to Reviewed", "error");
        } finally {
            setLoadingReviewed(false);
            setReviewedDialogOpen(false);
        }
    };

    const confirmBackToReviewed = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingBackToReviewed(true);
            await AdminGridBoardService.updateHwTplState(gridTpl.id, REVIEWED);
            handleSnackbar("Puzzle moved back to Reviewed", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving back to Reviewed:", err);
            handleSnackbar(err?.message || "Failed to update state", "error");
        } finally {
            setLoadingBackToReviewed(false);
            setBackToReviewedDialogOpen(false);
        }
    };

    const handlePuzzleTypeSelected = async (puzzleType) => {
        try {
            const result = await AdminGridBoardService.updateHwTplState(gridTpl.id, READY_TO_REVIEW, puzzleType);

            if (result && (result.code === "SUCCESS" || !result.code)) {
                handleSnackbar("Puzzle moved to review stage successfully", "success");
                navigate(`/admin/puzzle/grid_board/ready-to-review/${gridTpl.id}?fromStep=1`);
            } else {
                const errorMessage = result?.message || "Failed to update puzzle state";
                handleSnackbar(errorMessage, "error");
            }
        } catch (error) {
            Logger.error("Error moving puzzle to review stage:", error);
            const errorMessage = error?.message || "Error moving puzzle to review stage";
            handleSnackbar(errorMessage, "error");
        }
    };

    const handleFetchWordScores = async () => {
        if (!gridTpl?.wordsInUse) {
            handleSnackbar("No words found in puzzle", "warning");
            return;
        }

        try {
            setLoadingScores(true);
            if (!scoreDialogOpen) {
                setScoreDialogOpen(true);
            }

            // Extract words from wordsInUse (newline-separated string)
            const words = gridTpl.wordsInUse
                .split('\n')
                .map(word => word.trim())
                .filter(word => word.length > 0);

            if (words.length === 0) {
                handleSnackbar("No words found in puzzle", "warning");
                setScoreDialogOpen(false);
                return;
            }

            // Build query string with multiple words parameters
            const queryParams = words.map(word => `words=${encodeURIComponent(word)}`).join('&');
            const language = gridTpl?.language || 'english';
            const url = `/home/dic/${language}/multiple?validOnly=false&${queryParams}`;

            // Call API directly with full URL (no params object since we built the query string manually)
            const result = await ApiService.getData(url, null);

            if (result && Array.isArray(result)) {
                setWordScores(result);
            } else {
                handleSnackbar("Failed to fetch word scores", "error");
                setWordScores([]);
            }
        } catch (error) {
            Logger.error("Error fetching word scores:", error);
            handleSnackbar("Error fetching word scores", "error");
            setWordScores([]);
        } finally {
            setLoadingScores(false);
        }
    };

    const handleGenerateWordNetwork = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!gridTpl?.id) {
                handleSnackbar("No puzzle ID available", "error");
                return;
            }

            const result = await AdminGridBoardService.generateWordNetwork(gridTpl.id);

            if (result?.code === "SUCCESS") {
                handleSnackbar("Word network generated successfully", "success");
                // Refresh puzzle data
                setRefresh(prev => prev + 1);
            } else {
                handleSnackbar("Failed to generate word network", "error");
            }
        } catch (error) {
            Logger.error("Error generating word network:", error);
            handleSnackbar("Error generating word network", "error");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleRefreshWordNetwork = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            if (!gridTpl?.id) {
                handleSnackbar("No puzzle ID available", "error");
                return;
            }

            const language = gridTpl?.language || 'english';
            const result = await AdminGridBoardService.refreshWordNetwork(gridTpl.id, language);

            if (result?.code === "SUCCESS") {
                handleSnackbar("Word network refreshed successfully", "success");
                // Refresh puzzle data
                setRefresh(prev => prev + 1);
            } else {
                handleSnackbar("Failed to refresh word network", "error");
            }
        } catch (error) {
            Logger.error("Error refreshing word network:", error);
            handleSnackbar("Error refreshing word network", "error");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleSaveTitle = (definition, word) => {
        if (!definition || !word) {
            return;
        }

        // Open dialog to edit definition before saving
        setEditingDefinition(definition);
        setEditingWord(word);
        setOpenWordRedefineDialog(true);
    };

    const handleWordRedefineSave = async (editedDefinition, saveAsHiddenMessage = false) => {
        try {
            await AdminGridBoardService.setClue(editingWord, editedDefinition, gridTpl.id, saveAsHiddenMessage);
            handleSnackbar("Definition saved successfully", "success");
            setOpenWordRedefineDialog(false);
            setEditingDefinition("");
            setEditingWord("");
            // Refresh puzzle data
            setRefresh(prev => prev + 1);
        } catch (error) {
            Logger.error("Error saving definition:", error);
            handleSnackbar("Error saving definition", "error");
        }
    };

    const handleWordRedefineCancel = () => {
        setOpenWordRedefineDialog(false);
        setEditingDefinition("");
        setEditingWord("");
    };

    if (loading) {
        return (
            <Box sx={{ p: 0 }}>
                <Typography sx={{ mt: 4 }}>Loading...</Typography>
            </Box>
        );
    }

    if (fetchError || !gridTpl) {
        return (
            <Box sx={{ p: 0 }}>
                <Typography color="error">
                    {fetchError || "Puzzle not found"}
                </Typography>
            </Box>
        );
    }

    const hideMiddlePanel = ['READY_TO_REVIEW', 'REVIEWED', 'PUBLISHED'].includes(gridTpl?.state);
    const rightPanelSize = hideMiddlePanel ? 8 : 4;

    return (
        <Box sx={{ p: 0 }}>
            {/* Header with back button */}
            <BorderHeader
                onBack={handleBack}
                puzzle={gridTpl}
                puzzleType="grid"
                hiddenMessage={scatteredWord}
            />

            <Grid container spacing={2} sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                {/* Left side - Puzzle Preview */}
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666', mb: 1 }}>
                        Puzzle Preview
                    </Typography>
                    <Box
                        ref={gridContainerRef}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            backgroundColor: "#fafafa",
                            overflow: "hidden",
                            height: "350px",
                            mb: 2
                        }}
                    >
                        <Box
                            ref={gridContentRef}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                height: "100%",
                                transform: `scale(${gridScale})`,
                                transformOrigin: "center center"
                            }}
                        >
                            {square.length > 0 ? (
                                <Box>
                                    {/* Column indices header */}
                                    <Box sx={{ display: 'flex', gap: '1px', mb: 0.5 }}>
                                        {/* Empty space for row index column */}
                                        <Box sx={{ width: 24, height: 24 }} />
                                        {/* Column numbers */}
                                        {Array.from({ length: gridSize }, (_, colIndex) => (
                                            <Box
                                                key={colIndex}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    border: '1px solid transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '9px',
                                                    fontWeight: 'bold',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                {colIndex}
                                            </Box>
                                        ))}
                                    </Box>
                                    {/* Grid rows with row indices */}
                                    {square.map((row, rowIndex) => (
                                        <Box key={rowIndex} sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                                            {/* Row index */}
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '9px',
                                                    fontWeight: 'bold',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                {rowIndex}
                                            </Box>
                                            {/* Puzzle row */}
                                            <MiniPuzRow
                                                rowIndex={rowIndex}
                                                row={row}
                                                hiddenWord={(selectedWord?.word || selectedWord?.text || selectedManualWord?.word || selectedAssociatedWord?.word || selectedAssociatedWord?.text) || (selectedManualWord?.startRow != null && selectedManualWord?.startCol != null ? " " : "")}
                                                positions={(() => {
                                                    const base = (selectedWord?.positions || selectedManualWord?.positions || selectedAssociatedWord?.positions) || [];
                                                    const sel = selectedManualWord?.startRow != null && selectedManualWord?.startCol != null
                                                        ? { row: selectedManualWord.startRow, col: selectedManualWord.startCol }
                                                        : null;
                                                    if (sel && !base.some(p => p.row === sel.row && p.col === sel.col)) {
                                                        return [...base, sel];
                                                    }
                                                    return base;
                                                })()}
                                                onCellClick={handleCellClick}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography color="text.secondary" sx={{ p: 2 }}>
                                    No puzzle grid data available
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {cellsFullyFilled && (
                        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                            All cells are filled. Fill Assoc and Rearrange are disabled.
                        </Alert>
                    )}
                    {/* Action Buttons - same layout as crossword */}
                    {gridTpl?.state && gridTpl.state !== '' && !['DISCARDED', 'DISABLED'].includes(gridTpl.state) && (() => {
                        const actionsDisabled = ['REVIEWED', 'PUBLISHED', 'DISCARDED', 'DISABLED'].includes(gridTpl?.state);
                        return (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={<FactCheckIcon />} onClick={() => setValidationDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }} size="small">
                                        Validation
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={loadingScores ? <CircularProgress size={16} color="inherit" /> : <ScoreIcon />} onClick={handleFetchWordScores} disabled={loadingScores} sx={{ color: '#ed6c02', borderColor: '#ed6c02', '&:hover': { backgroundColor: '#ed6c02', color: 'white', borderColor: '#ed6c02' }, whiteSpace: 'nowrap' }} size="small">
                                        Score
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={<OpenInNewIcon />} onClick={() => setNetworkDialogOpen(true)} disabled={loadingApplyTitleHook} sx={{ whiteSpace: 'nowrap' }} size="small">
                                        Network
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={loadingApplyTitleHook ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />} onClick={handleApplyTitleHook} disabled={gridTpl?.state !== DRAFT || actionsDisabled || loadingApplyTitleHook} sx={{ color: '#9c27b0', borderColor: '#9c27b0', '&:hover': { backgroundColor: '#9c27b0', color: 'white', borderColor: '#9c27b0' }, whiteSpace: 'nowrap' }} size="small">
                                        Hook The Title
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={loadingRearrange ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />} onClick={handleRearrangeWords} disabled={gridTpl?.state !== DRAFT || actionsDisabled || cellsFullyFilled || loadingApplyTitleHook || loadingRearrange} sx={{ color: '#1976d2', borderColor: '#1976d2', '&:hover': { backgroundColor: '#1976d2', color: 'white', borderColor: '#1976d2' }, whiteSpace: 'nowrap' }} size="small">
                                        Rearrange
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={<MenuBookIcon />} onClick={() => setFillCommonDialogOpen(true)} disabled={gridTpl?.type === "CROSSWORD" || gridTpl?.state !== DRAFT || actionsDisabled || cellsFullyFilled || loadingApplyTitleHook} sx={{ color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { backgroundColor: '#2e7d32', color: 'white', borderColor: '#2e7d32' }, whiteSpace: 'nowrap' }} size="small">
                                        Fill
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={loadingFillRandom ? <CircularProgress size={16} color="inherit" /> : <ShuffleIcon />}
                                        onClick={handleFillRandomLetters}
                                        disabled={gridTpl?.type === "CROSSWORD" || gridTpl?.state !== DRAFT || actionsDisabled || cellsFullyFilled || loadingApplyTitleHook || loadingFillRandom}
                                        sx={{ color: '#00897b', borderColor: '#00897b', '&:hover': { backgroundColor: '#00897b', color: 'white', borderColor: '#00897b' }, whiteSpace: 'nowrap' }}
                                        size="small"
                                    >
                                        Fill Random letter
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <IconButton onClick={handleStateBack} disabled={gridTpl?.state === DRAFT || loadingApplyTitleHook || loadingDraft || loadingReadyToReview || loadingReviewed || loadingBackToReviewed} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                                <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', minWidth: 0 }}>
                                    <Button variant="outlined" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handleDraftClick} startIcon={loadingDraft ? <CircularProgress size={16} color="inherit" /> : <ArrowBackIcon />} disabled={gridTpl?.state !== READY_TO_REVIEW || loadingApplyTitleHook || loadingDraft}>
                                        Draft
                                    </Button>
                                    <Button variant="outlined" color="success" sx={{ minWidth: 120, whiteSpace: 'nowrap' }} size="small" onClick={handleReadyForReview} startIcon={<CheckCircleIcon />} disabled={gridTpl?.state !== DRAFT || loadingApplyTitleHook}>
                                        Ready to Review
                                    </Button>
                                    <Button variant="outlined" color="primary" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handleReviewedClick} startIcon={loadingReviewed || loadingBackToReviewed ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />} disabled={((gridTpl?.state !== READY_TO_REVIEW || !isManager) && gridTpl?.state !== PUBLISHED) || loadingApplyTitleHook || loadingReviewed || loadingBackToReviewed}>
                                        Reviewed
                                    </Button>
                                    <Button variant="outlined" sx={{ minWidth: 90, whiteSpace: 'nowrap', color: '#d32f2f', borderColor: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f', color: 'white', borderColor: '#d32f2f' } }} size="small" onClick={() => { setWordToDelete(null); setDiscardDialogOpen(true); }} startIcon={<DeleteIcon />} disabled={actionsDisabled}>
                                        Discard
                                    </Button>
                                </Box>
                                <IconButton onClick={handleStateForward} disabled={(gridTpl?.state === PUBLISHED || gridTpl?.state === REVIEWED) || (gridTpl?.state === READY_TO_REVIEW && !isManager) || loadingApplyTitleHook || loadingDraft || loadingReadyToReview || loadingReviewed || loadingBackToReviewed} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                    <ArrowForwardIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            {gridTpl?.state === "REVIEWED" && (
                                <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <span>
                                        This puzzle is <strong>REVIEWED</strong>. It will be published on schedule.
                                    </span>
                                </Alert>
                            )}
                        </Box>
                        );
                    })()}
                </Grid>

                {/* Middle - Word candidates or Manual form */}
                {!hideMiddlePanel && (
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                            {modeRef.current === 'assoc' ? 'Association Search' :
                                modeRef.current === 'ai' ? 'AI Associations' :
                                    'Manual Entry'}
                        </Typography>
                        <ToggleButtonGroup
                            value={modeRef.current}
                            exclusive
                            onChange={handleModeChange}
                            size="small"
                        >
                            <ToggleButton value="assoc">Assoc</ToggleButton>
                            <ToggleButton value="manual">Manual</ToggleButton>
                            <ToggleButton value="ai">AI</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    {modeRef.current === 'assoc' ? (
                        <GridCanAssoc
                            gridTpl={gridTpl}
                            square={square}
                            onWordAdd={handleWordAdd}
                            onShowSnackbar={handleSnackbar}
                            onWordSelect={handleWordSelect}
                            onRefresh={() => setRefresh(prev => prev + 1)}
                            highlightedWordFromPreview={(selectedWord?.text ?? selectedWord?.word) || null}
                            placementWordFromFill={placementWordFromFill}
                            onClearPlacementWordFromFill={() => setPlacementWordFromFill(null)}
                        />
                    ) : modeRef.current === 'ai' ? (
                        <GridCandAI
                            onWordAdd={handleManualWordAdd}
                            onWordSelect={handleManualWordSelect}
                            selectedManualWord={selectedManualWord}
                            onShowSnackbar={handleSnackbar}
                            square={square}
                            gridTpl={gridTpl}
                            onRefresh={() => setRefresh(prev => prev + 1)}
                        />
                    ) : (
                        <GridCandManual
                            onWordAdd={handleManualWordAdd}
                            onWordSelect={handleManualWordSelect}
                            selectedManualWord={selectedManualWord}
                            onShowSnackbar={handleSnackbar}
                            square={square}
                            gridTpl={gridTpl}
                        />
                    )}
                </Grid>
                )}

                {/* Right side - Word meaning */}
                <Grid size={{ xs: 12, md: rightPanelSize }} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                            Word Meaning
                        </Typography>
                        <Tooltip title="View Banned Words">
                            <IconButton
                                size="small"
                                onClick={() => setOpenBannedWordsPopup(true)}
                                sx={{ color: 'error.main' }}
                            >
                                <BlockIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {(selectedWord || selectedManualWord || selectedAssociatedWord || selectedNetworkWord) ? (
                            <WordMeaning
                                selectedWord={(selectedNetworkWord || selectedWord?.word || selectedWord?.text || selectedManualWord?.word || selectedAssociatedWord?.word) || ""}
                                onSaveDefinition={handleSaveTitle}
                                gridTplId={gridTpl?.id}
                                language={gridTpl?.language}
                                fullHeight={true}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                Select a word from the {modeRef.current === 'assoc' ? 'puzzle words' : 'results'} or word network to see its meaning
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Snackbar */}
            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />

            {/* Discard Confirmation Dialog */}
            <ConfirmDialog
                open={discardDialogOpen}
                setOpen={(open) => {
                    setDiscardDialogOpen(open);
                    if (!open) {
                        setWordToDelete(null);
                    }
                }}
                title={wordToDelete ? "Delete Word" : "Discard Puzzle"}
                message={wordToDelete ? `Would you like to delete the word "${wordToDelete}"?` : "Would you like to discard this puzzle?"}
                targetName=""
                targetId=""
                callback={wordToDelete ? handleConfirmDeleteWord : handleDiscardPuzzle}
            />

            {/* Disable Confirmation Dialog */}
            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable Puzzle"
                message="Would you like to disable this puzzle? It will be withdrawn from publication."
                warningAlert="If the puzzle is set to 'DISABLED' now, the puzzle will be disappeared on all user's page 1 week later."
                targetName=""
                targetId=""
                callback={handleDisablePuzzle}
            />

            {/* Puzzle Type Selection Dialog */}
            <VerifyDialog
                open={puzzleTypeDialogOpen}
                onClose={() => setPuzzleTypeDialogOpen(false)}
                puzzleId={gridTpl?.id}
                requestPuzzle={AdminGridBoardService.requestHwTpl}
                defaultPuzzleType="WORD_SEARCH"
                allowedPuzzleTypes={['WORD_SEARCH', 'HIDDEN_MESSAGE']}
                onPuzzleTypeSelected={handlePuzzleTypeSelected}
                onShowSnackbar={handleSnackbar}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!gridTpl?.id) return;
                    await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, null);
                    setRefresh((prev) => prev + 1);
                }}
            />

            <ResolvePuzzleDialog
                open={resolveDialogOpen}
                onClose={() => setResolveDialogOpen(false)}
                gridTpl={gridTpl}
                resolveType={resolveDialogType}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!gridTpl?.id) return;
                    await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, null);
                }}
                onRefresh={() => setRefresh((prev) => prev + 1)}
                onShowSnackbar={handleSnackbar}
            />

            {/* Banned Words Popup */}
            <BannedWordsPopup
                open={openBannedWordsPopup}
                onClose={() => setOpenBannedWordsPopup(false)}
                language={gridTpl?.language || "english"}
            />

            <AddBannedWordDialog
                open={openAddBannedWordDialog}
                onClose={() => { setOpenAddBannedWordDialog(false); setSelectedWordForBanned(""); }}
                onAdd={async (word, category, wordLanguage) => {
                    setLoadingAddBanned(true);
                    try {
                        await BannedService.addWord(category, word, "", wordLanguage);
                        handleSnackbar(`"${word}" added to banned words`, "success");
                        setOpenAddBannedWordDialog(false);
                        setSelectedWordForBanned("");
                    } catch (err) {
                        Logger.error("Error adding banned word:", err);
                        handleSnackbar(err?.message ?? "Failed to add banned word", "error");
                    } finally {
                        setLoadingAddBanned(false);
                    }
                }}
                selectedWord={selectedWordForBanned}
                language={gridTpl?.language ?? "english"}
                loading={loadingAddBanned}
            />

            {/* WordRedefine Dialog */}
            <WordRedefine
                open={openWordRedefineDialog}
                onClose={handleWordRedefineCancel}
                onSave={handleWordRedefineSave}
                word={editingWord}
                definition={editingDefinition}
                hiddenMessage={scatteredWord?.text}
            />

            {/* Title Hook Confirmation Dialog */}
            <ConfirmDialog
                open={showTitleHookDialog}
                setOpen={setShowTitleHookDialog}
                title="Hook The Title"
                message={
                    <Box>
                        <span>Would you like to change the title using the Bedrock?</span>
                        {gridTpl?.type === "HIDDEN_MESSAGE" && !scatteredWord?.text && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                "Hidden Message" type puzzle should have the message
                            </Alert>
                        )}
                    </Box>
                }
                callback={confirmApplyTitleHook}
            />

            {/* Word Score Dialog */}
            <WordScoreDialog
                open={scoreDialogOpen}
                onClose={() => setScoreDialogOpen(false)}
                wordScores={wordScores}
                loadingScores={loadingScores}
                language={gridTpl?.language || "english"}
                onRefresh={async () => {
                    // Close dialog first
                    setScoreDialogOpen(false);
                    // Small delay to ensure dialog closes
                    setTimeout(async () => {
                        // Refresh word scores and reopen dialog
                        setScoreDialogOpen(true);
                        await handleFetchWordScores();
                    }, 100);
                }}
            />

            <ConfirmDialog open={draftDialogOpen} setOpen={setDraftDialogOpen} title="Move back to Draft?" message="Change puzzle state to Draft? You can edit the puzzle again." callback={confirmDraft} />
            <ConfirmDialog open={readyToReviewDialogOpen} setOpen={setReadyToReviewDialogOpen} title="Move back to Ready to Review?" message="Change puzzle state from Reviewed to Ready to Review?" callback={confirmReadyToReview} />
            <ConfirmDialog open={reviewedDialogOpen} setOpen={setReviewedDialogOpen} title="Mark as Reviewed" message="Would you like to mark this puzzle as Reviewed?" targetName="" targetId="" callback={confirmReviewed} />
            <ConfirmDialog
                open={backToReviewedDialogOpen}
                setOpen={setBackToReviewedDialogOpen}
                title="Move back to Reviewed"
                message="Would you like to move this puzzle back to Reviewed state?"
                warningAlert={
                    <>
                        If this puzzle is played 1 time or more, it can&apos;t be moved back to &quot;REVIEWED&quot;. It could be &quot;DISABLED&quot; state, request it to Young.
                        <br />
                        <br />
                        If the puzzle is set to &quot;DISABLED&quot; now, the puzzle will be disappeared on all user&apos;s page 1 week later.
                    </>
                }
                targetName=""
                targetId=""
                callback={confirmBackToReviewed}
            />

            <ValidationDialog
                open={validationDialogOpen}
                onClose={() => setValidationDialogOpen(false)}
                onResolve={(resolveType) => {
                    setValidationDialogOpen(false);
                    setResolveDialogType(resolveType);
                    setResolveDialogOpen(true);
                }}
                gridTpl={gridTpl}
            />

            {/* Word Network Dialog for Mobile */}
            <WordNetworkDialog
                open={networkDialogOpen}
                onClose={() => setNetworkDialogOpen(false)}
                hasWordNetwork={hasWordNetwork}
                wordNetworkData={wordNetworkData}
                onDeleteWord={handleDeleteWordFromNetwork}
                onNodeSelect={(word) => {
                    // Clear other selections when selecting from network
                    setSelectedWord(null);
                    setSelectedAssociatedWord(null);
                    setSelectedManualWord(null);
                    setSelectedNetworkWord(word);
                }}
                onRefresh={handleRefreshWordNetwork}
                onGenerateWordNetwork={handleGenerateWordNetwork}
            />
            <GridFillDialog
                open={fillCommonDialogOpen}
                onClose={() => setFillCommonDialogOpen(false)}
                title="Fill"
                puzzleTitle={gridTpl?.title}
                puzzleType={gridTpl?.type}
                tags={gridTpl?.tags ?? gridTpl?.template?.tags ?? []}
                gridId={gridTpl?.id}
                language={gridTpl?.language ?? "english"}
                cellsFullyFilled={cellsFullyFilled}
                emptyCellCount={emptyCellCount}
                filledCoordinates={filledCoordinates}
                wordsNotInUse={wordsNotInUse}
                onPlaceWord={(item) => addWordFromResult(item)}
                onPlaceWords={(items) => addWordsFromResult(items)}
                onNotInUsePlaceWord={(word) => {
                    if (word) {
                        setPlacementWordFromFill(word);
                        modeRef.current = "assoc";
                        setMode("assoc");
                    }
                }}
                onTab2Add={handleFillPatternAdd}
                adding={loadingMatchKeywordAdd || loadingFillPatternAdd}
                onReadyToReview={() => {
                    setFillCommonDialogOpen(false);
                    handleReadyForReview();
                }}
                readyToReviewDisabled={gridTpl?.state !== DRAFT || loadingApplyTitleHook}
                readyToReviewLoading={false}
                onAddBannedWord={(word) => {
                    if (!word?.trim()) return;
                    setSelectedWordForBanned(word.trim());
                    setOpenAddBannedWordDialog(true);
                }}
                onShowSnackbar={handleSnackbar}
                onFillMoreSuccess={() => setRefresh(prev => prev + 1)}
            />
        </Box>
    );
}

